import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { stockUnitsApi } from '../stockApi';
import { UNIT_STATUS, UNIT_STATUS_LABEL } from '../stockTypes';
import type { StockUnitListItem } from '../stockTypes';

/**
 * Mark one unit in stock, damaged, or under repair — the three states that still HOLD it.
 *
 * **Not the write-off, and the split is the point.** Disposed and Stolen take the unit OFF the
 * owned tally, which is a different claim and a different key (`stock-units.write-off`); the
 * server refuses either through this endpoint, so the picker offers neither rather than
 * producing a rejection the reader cannot act on.
 *
 * **It is a DEDICATED edit, not the whole unit form.** The reader came to say a laptop came back
 * broken, and `<StockUnitModal>` would re-ask the serial, the facility, the tenure, the dates and
 * the cost to change one field — which is the rule a card's own Edit already follows one surface
 * along.
 *
 * **It carries every other field forward from the ROW, and fetches nothing.** `StockUnitListItem`
 * happens to hold all nine of `UpdateStockUnitRequest`'s other fields, so the PUT restates what
 * the record already says rather than blanking what the form cannot see — which is the
 * government-ID lesson, and the reason this is worth stating: the day the request grows a field
 * the row does not carry, this modal owes a `stockUnitsApi.get` before it can keep using the PUT.
 */
export function SetStockUnitConditionModal({
  open,
  unit,
  onClose,
  onSaved,
}: {
  open: boolean;
  unit: StockUnitListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [status, setStatus] = useState(String(unit.status));

  /*
    Seeded EMPTY rather than from the record, because the row does not carry the stored reason —
    and asking for it fresh is honest here: a unit moving from damaged to under repair has a new
    reason, and one carried across would state the old one as though nobody had looked.
  */
  const [statusReason, setStatusReason] = useState('');

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  // The server clears the reason on In stock, so the form stops asking for one there.
  const outOfService = Number(status) !== UNIT_STATUS.inStock;
  const valid = !outOfService || statusReason.trim().length > 0;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await stockUnitsApi.update(unit.id, {
        serialNumber: unit.serialNumber,
        storageFacilityId: unit.storageFacilityId,
        ownership: unit.ownership,

        /*
          PRESERVED, because this form cannot see it.

          The update applies what it is given, and this request is rebuilt from the row on
          every save — so a field left out would be a condition change silently wiping the
          unit's fund. That is the government-ID rule reaching a second module: a form that
          cannot show a value must not be able to destroy it.
        */
        fund: unit.fund,

        acquisitionDate: unit.acquisitionDate,
        leaseEndDate: unit.leaseEndDate,
        leasedFrom: unit.leasedFrom,
        termEndDate: unit.termEndDate,
        unitCost: unit.unitCost,
        status: Number(status) as StockUnitListItem['status'],
        statusReason: statusReason.trim() || null,
        notes: unit.notes,
      });

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not set the condition.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'unit-condition' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Set condition"
      size="md"
      id="unit-condition-modal"
      confirm={{
        id: 'unit-condition-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="unit-condition-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="unit-condition-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {/* The unit is not asked for — the reader chose it by opening a row's menu. */}
        <PreviewTable
          variant="table-display"
          rows={[
            { label: 'Serial', value: unit.serialNumber, mono: true },
            { label: 'Item', value: `${unit.sku} — ${unit.itemName}` },
            { label: 'Facility', value: unit.storageFacilityName },
            { label: 'Condition', value: UNIT_STATUS_LABEL[unit.status] },
          ]}
        />

        <Select
          id="unit-condition-status"
          label="Condition"
          description="Damaged and under repair still count toward what is held — they are unusable rather than gone. Taking a unit off the tally is the write-off."
          value={status}
          onChange={setStatus}
          sort="none"
          clearable={false}
          required
          disabled={saving}
          options={[UNIT_STATUS.inStock, UNIT_STATUS.damaged, UNIT_STATUS.underRepair].map(
            (value) => ({ value: String(value), label: UNIT_STATUS_LABEL[value] }),
          )}
          error={error?.fieldError('status')}
        />

        {/*
          Drawn only where it applies rather than disabled, because on In stock there is nothing
          left to explain — and the server clears whatever a request carries there anyway.
        */}
        {outOfService ? (
          <TextField
            id="unit-condition-status-reason"
            label="Reason"
            description="Why it is out of service. Recorded on the unit, and on the ledger line."
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            maxLength={500}
            required
            disabled={saving}
            error={
              statusReason.trim().length > 0
                ? error?.fieldError('statusReason')
                : 'Say why this unit is out of service.'
            }
          />
        ) : null}
      </div>
    </Modal>
  );
}
